import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faTerminal, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDevelopAppTerminal } from "./DevelopAppTerminalContext";
import {
  buildTerminalEmbedUrl,
  enrichTerminalDeviceChipsets,
  fetchAndConnectTerminal,
} from "./developAppTerminalApi";
import "./Devices.scss";

const CONNECT_REQUEST = "deepedge:terminal-connect";
const CONNECT_RESULT = "deepedge:terminal-connected";
const DOCK_MARGIN = 20;
const CENTER_ZONE_RATIO = 0.34;

const clampDockPosition = (x, y, dockWidth, dockHeight) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const centerStart = viewportWidth * (0.5 - CENTER_ZONE_RATIO / 2);
  const centerEnd = viewportWidth * (0.5 + CENTER_ZONE_RATIO / 2);
  const dockCenterX = x + dockWidth / 2;

  let nextX = x;
  if (dockCenterX >= centerStart && dockCenterX <= centerEnd) {
    nextX =
      dockCenterX < viewportWidth / 2
        ? DOCK_MARGIN
        : viewportWidth - dockWidth - DOCK_MARGIN;
  } else {
    nextX = Math.max(
      DOCK_MARGIN,
      Math.min(x, viewportWidth - dockWidth - DOCK_MARGIN)
    );
  }

  const nextY = Math.max(
    DOCK_MARGIN,
    Math.min(y, viewportHeight - dockHeight - DOCK_MARGIN)
  );

  return { x: nextX, y: nextY };
};

export default function DevelopAppTerminalHost() {
  const location = useLocation();
  const {
    open,
    minimized,
    device,
    credentials,
    frameRef,
    nudgeResize,
    minimizeTerminal,
    restoreTerminal,
    closeTerminal,
    openTerminal,
  } = useDevelopAppTerminal();

  const panelRef = useRef(null);
  const dockRef = useRef(null);
  const dragStateRef = useRef(null);
  const [dockPos, setDockPos] = useState(null);
  const [dockDragging, setDockDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setDockPos(null);
      dragStateRef.current = null;
      setDockDragging(false);
    }
  }, [open]);

  const getDockRect = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return null;
    return panel.getBoundingClientRect();
  }, []);

  const handleDockPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();

      const rect = getDockRect();
      if (!rect) return;

      dragStateRef.current = {
        pointerId: event.pointerId,
        startPointerX: event.clientX,
        startPointerY: event.clientY,
        startX: dockPos?.x ?? rect.left,
        startY: dockPos?.y ?? rect.top,
        moved: false,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [dockPos, getDockRect]
  );

  const handleDockPointerMove = useCallback(
    (event) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - drag.startPointerX;
      const deltaY = event.clientY - drag.startPointerY;
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        drag.moved = true;
        setDockDragging(true);
      }
      if (!drag.moved) return;

      setDockPos({
        x: drag.startX + deltaX,
        y: drag.startY + deltaY,
      });
    },
    []
  );

  const finishDockDrag = useCallback(
    (event) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }

      if (!drag.moved) {
        restoreTerminal();
      } else {
        const rect = getDockRect();
        const width = rect?.width ?? 280;
        const height = rect?.height ?? 56;
        const deltaX = event.clientX - drag.startPointerX;
        const deltaY = event.clientY - drag.startPointerY;
        const currentX = drag.startX + deltaX;
        const currentY = drag.startY + deltaY;
        setDockPos(clampDockPosition(currentX, currentY, width, height));
      }

      dragStateRef.current = null;
      setDockDragging(false);
    },
    [getDockRect, restoreTerminal]
  );

  useEffect(() => {
    if (!open || !device) return undefined;
    let cancelled = false;

    enrichTerminalDeviceChipsets()
      .then(() => {
        if (cancelled) return;
        try {
          frameRef.current?.contentWindow?.postMessage(
            { type: "deepedge:reload-devices" },
            "*"
          );
        } catch {
          // ignore
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [open, device, frameRef]);

  useEffect(() => {
    if (!open || !device) return undefined;

    const onMessage = async (event) => {
      const data = event.data || {};
      if (data.type !== CONNECT_REQUEST) return;
      const requestedId = String(data.deviceId || "");
      const currentId = String(
        credentials?.deviceId || device?.client_id || device?.device_code || ""
      );
      const frameWindow = frameRef.current?.contentWindow;
      const reply = (payload) => {
        try {
          frameWindow?.postMessage(
            {
              type: CONNECT_RESULT,
              deviceId: requestedId || currentId,
              ...payload,
            },
            "*"
          );
        } catch {
          // ignore
        }
      };

      try {
        const recordId =
          data.recordId ||
          (!requestedId || requestedId === currentId ? device._id : "");
        if (!recordId) {
          reply({
            ok: false,
            error: "Open Develop App from that device page first to load credentials.",
          });
          return;
        }

        const snapshot =
          recordId === device._id
            ? device
            : {
                ...device,
                _id: recordId,
                client_id: requestedId || device.client_id,
                device_ip: data.deviceLanIp || device.device_ip,
              };

        const { credentials: nextCredentials, connectResult } =
          await fetchAndConnectTerminal(snapshot, {
            deviceId: requestedId || currentId,
            deviceLanIp:
              data.deviceLanIp || credentials?.deviceLanIp || snapshot.device_ip,
            recordId,
          });
        if (!requestedId || requestedId === currentId) {
          openTerminal({ device, credentials: nextCredentials });
        }
        reply({
          ok: true,
          mode: connectResult?.mode,
          remoteHost: connectResult?.remoteHost,
          remotePort: connectResult?.remotePort,
          reversePort: connectResult?.reversePort,
        });
      } catch (err) {
        reply({ ok: false, error: err?.message || "Connect failed" });
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, device, credentials, frameRef, openTerminal]);

  // Keep session alive across pages, but hide on login.
  if (!open || !device) return null;
  if (location.pathname === "/login" || location.pathname.startsWith("/login/")) {
    return null;
  }

  // Header shows portal remote (DevTools Remote Address), not device LAN IP.
  const titleIp = (() => {
    const host = window.location?.hostname;
    if (!host || host === "localhost") return " · 127.0.0.1";
    return ` · ${host}`;
  })();

  return (
    <>
      {!minimized ? (
        <div
          className="device-terminal-backdrop"
          onClick={minimizeTerminal}
          aria-hidden
        />
      ) : null}

      <div
        ref={panelRef}
        className={`device-terminal-panel${minimized ? " is-minimized" : ""}${
          minimized && dockPos ? " is-positioned" : ""
        }${dockDragging ? " is-dragging" : ""}`}
        style={
          minimized && dockPos
            ? {
                left: `${dockPos.x}px`,
                top: `${dockPos.y}px`,
                right: "auto",
                bottom: "auto",
              }
            : undefined
        }
        role="dialog"
        aria-label="Develop App terminal"
        aria-modal={!minimized}
      >
        {!minimized ? (
          <div className="device-terminal-modal__toolbar">
            <div className="device-terminal-modal__toolbar-copy">
              <span className="device-terminal-modal__eyebrow">Develop App</span>
              <strong>
                {device?.name || "Device"}
                {titleIp}
              </strong>
            </div>
            <div className="device-terminal-modal__actions">
              <button
                type="button"
                className="device-terminal-modal__icon-btn"
                onClick={minimizeTerminal}
                aria-label="Minimize terminal"
                title="Minimize"
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <button
                type="button"
                className="device-terminal-modal__icon-btn device-terminal-modal__icon-btn--danger"
                onClick={closeTerminal}
                aria-label="Close terminal"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        ) : null}

        <div className="device-terminal-panel__body">
          <iframe
            ref={frameRef}
            key={String(device?._id || credentials?.deviceId || "")}
            title={`${device?.name || "Device"} terminal`}
            src={buildTerminalEmbedUrl(device, credentials || {})}
            className="device-terminal-modal__frame"
            allow="clipboard-read; clipboard-write"
            onLoad={(event) => {
              frameRef.current = event.currentTarget;
              nudgeResize();
              setTimeout(nudgeResize, 50);
              setTimeout(nudgeResize, 200);
              setTimeout(nudgeResize, 500);
              enrichTerminalDeviceChipsets()
                .then(() => {
                  try {
                    event.currentTarget.contentWindow?.postMessage(
                      { type: "deepedge:reload-devices" },
                      "*"
                    );
                  } catch {
                    // ignore
                  }
                })
                .catch(() => {});
            }}
          />
        </div>

        {minimized ? (
          <div
            ref={dockRef}
            className="device-terminal-dock"
            onPointerDown={handleDockPointerDown}
            onPointerMove={handleDockPointerMove}
            onPointerUp={finishDockDrag}
            onPointerCancel={finishDockDrag}
          >
            <button
              type="button"
              className="device-terminal-dock__main"
              title="Drag to move · click to restore"
            >
              <span className="device-terminal-dock__icon">
                <FontAwesomeIcon icon={faTerminal} />
              </span>
              <span className="device-terminal-dock__copy">
                <span className="device-terminal-dock__eyebrow">Develop App</span>
                <strong>
                  {device?.name || "Device"}
                  {titleIp}
                </strong>
              </span>
            </button>
            <button
              type="button"
              className="device-terminal-dock__close"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={closeTerminal}
              aria-label="Close terminal"
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
